import { useCallback, useEffect, useRef, useState } from 'react';
import { activityContextFromParams } from '../../api/context';
import { resolveApiConfig } from '../../api/client';
import { pickBestScope } from '../../api/feature-payload';
import { useAppParams } from '../../api/params';

export type Role = 'user' | 'assistant';
export type Message = {
  id: string;
  role: Role;
  content: string;
  pending?: boolean;
  /**
   * Traza de actividad del asistente para este mensaje: las fases
   * intermedias (thinking → selecting → reading) por las que pasó antes
   * de responder, con los módulos que consultó. Se acumula en vivo y
   * sobrevive a la respuesta para mostrarse como resumen colapsable.
   */
  steps?: ThreadStatus[];
};

export type ThreadStatusState = 'thinking' | 'selecting' | 'reading';

/**
 * Estado intermedio que el asistente emite antes de responder. Los estados
 * `selecting` y `reading` incluyen los nombres de los módulos que consulta.
 */
export type ThreadStatus = {
  state: ThreadStatusState;
  modules?: string[];
};

type ThreadStreamEvent =
  | { type: 'status'; state: ThreadStatusState; modules?: string[] }
  | { type: 'text_delta'; delta: string };

let idCounter = 1;
const nextId = () => `m-${idCounter++}`;

/**
 * Añade una fase a la traza. Si llega el mismo estado que el último paso
 * (p. ej. varios `reading`), actualizamos sus módulos en vez de duplicar
 * la fila, de modo que la traza quede en una secuencia limpia.
 */
function appendStep(steps: ThreadStatus[], next: ThreadStatus): ThreadStatus[] {
  const last = steps[steps.length - 1];
  if (last && last.state === next.state) {
    return [...steps.slice(0, -1), next];
  }
  return [...steps, next];
}

/**
 * Procesa los bloques SSE completos del buffer (separados por una línea en
 * blanco) y devuelve el resto incompleto para la siguiente lectura.
 */
function drainSSEBuffer(
  buffer: string,
  onEvent: (event: ThreadStreamEvent) => void
): string {
  const blocks = buffer.split(/\r?\n\r?\n/);
  // El último bloque puede estar incompleto: lo devolvemos al buffer.
  const remainder = blocks.pop() ?? '';
  for (const block of blocks) {
    if (!block.trim()) continue;
    const event = parseSSEBlock(block);
    if (event) onEvent(event);
  }
  return remainder;
}

/**
 * Parsea un bloque SSE a un evento tipado. Tolera tanto JSON en una sola
 * línea (`data: {...}`) como JSON pretty-printed en varias líneas tras el
 * primer `data:`.
 */
function parseSSEBlock(block: string): ThreadStreamEvent | null {
  const lines = block.split(/\r?\n/);
  const dataLines: string[] = [];
  let collecting = false;
  for (const line of lines) {
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''));
      collecting = true;
    } else if (line.startsWith('event:') || line.startsWith(':')) {
      // Línea de tipo de evento o comentario/heartbeat: se ignora.
      collecting = false;
    } else if (collecting) {
      // Continuación de un payload JSON multilínea.
      dataLines.push(line);
    }
  }
  if (dataLines.length === 0) return null;
  const payload = dataLines.join('\n').trim();
  if (!payload || payload === '[DONE]') return null;
  try {
    const data = JSON.parse(payload);
    if (typeof data?.delta === 'string') {
      return { type: 'text_delta', delta: data.delta };
    }
    if (typeof data?.state === 'string') {
      return {
        type: 'status',
        state: data.state as ThreadStatusState,
        modules: Array.isArray(data.modules) ? data.modules : undefined
      };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Thread chat against the real backend.
 *
 * The first user message triggers two requests in sequence:
 *   1. POST /api/v2/activity/thread → returns a uuid (JSON)
 *   2. POST /api/v2/activity/{uuid}/thread/message → SSE stream
 *      (event: status / event: text_delta), parsed by drainSSEBuffer.
 *
 * v2 is required: the v1 message endpoint replies in text/plain, which
 * our SSE parser can't surface, so the assistant bubble stayed empty.
 *
 * Subsequent messages reuse the cached uuid and only call (2). We never
 * create the thread eagerly — opening the chat without typing should not
 * burn a backend call.
 */
export function useThread() {
  const params = useAppParams();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Cached thread uuid for the lifetime of this hook. We hold it in a ref
  // (not state) so a fast re-call doesn't race a not-yet-flushed setState.
  const threadUuidRef = useRef<string | null>(null);
  const creatingRef = useRef<Promise<string> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const ensureThreadUuid = useCallback(async (): Promise<string> => {
    if (threadUuidRef.current) return threadUuidRef.current;
    if (creatingRef.current) return creatingRef.current;
    if (!params) throw new Error('Missing params');
    // Chat always opens with the richest available context (module if we
    // have it, else section, else course) — Phia should know about the
    // user's exact position, not just "the course".
    const ctx = activityContextFromParams(params, pickBestScope(params));
    if (!ctx) throw new Error('Missing course context');

    const p = (async () => {
      const { baseUrl, token } = resolveApiConfig(params);
      const res = await fetch(`${baseUrl}/api/v2/activity/thread`, {
        method: 'POST',
        headers: {
          'X-API-KEY': token,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ ...ctx, type: 'chat' })
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${errBody.slice(0, 200)}`);
      }
      const data = (await res.json()) as { uuid?: string };
      const uuid = data?.uuid;
      if (!uuid) throw new Error('Thread response had no uuid');
      threadUuidRef.current = uuid;
      return uuid;
    })();
    creatingRef.current = p;
    try {
      return await p;
    } finally {
      creatingRef.current = null;
    }
  }, [params]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming || !params) return;

      const userMsg: Message = { id: nextId(), role: 'user', content: trimmed };
      const assistantMsg: Message = {
        id: nextId(),
        role: 'assistant',
        content: '',
        pending: true,
        steps: []
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      const recordStep = (step: ThreadStatus) =>
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? { ...m, steps: appendStep(m.steps ?? [], step) }
              : m
          )
        );
      const appendChunk = (chunk: string) =>
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id ? { ...m, content: m.content + chunk } : m
          )
        );
      const markDone = () =>
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id ? { ...m, pending: false } : m
          )
        );
      const replaceWith = (msg: string) =>
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id ? { ...m, content: msg, pending: false } : m
          )
        );

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const uuid = await ensureThreadUuid();
        const { baseUrl, token } = resolveApiConfig(params);

        const res = await fetch(
          `${baseUrl}/api/v2/activity/${uuid}/thread/message`,
          {
            method: 'POST',
            signal: controller.signal,
            headers: {
              'X-API-KEY': token,
              'Content-Type': 'application/json',
              Accept: 'text/plain, application/json'
            },
            body: JSON.stringify({ message: trimmed })
          }
        );

        if (!res.ok) {
          const errBody = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status} ${errBody.slice(0, 200)}`);
        }

        const reader = res.body?.getReader();
        if (!reader) {
          const fallback = (await res.text()).replace(/^\s+/, '');
          replaceWith(fallback);
        } else {
          // La respuesta es un stream SSE (event: status / event: text_delta).
          const onEvent = (event: ThreadStreamEvent) => {
            if (event.type === 'status') {
              recordStep({ state: event.state, modules: event.modules });
              return;
            }
            if (event.delta) appendChunk(event.delta);
          };

          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            buffer = drainSSEBuffer(buffer, onEvent);
          }
          // Procesar cualquier evento que quede al cerrar el stream.
          buffer += decoder.decode();
          if (buffer.trim()) {
            const event = parseSSEBlock(buffer);
            if (event) onEvent(event);
          }
          markDone();
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        const msg = (e as Error).message;
        setError(e as Error);
        replaceWith(`No he podido responder. (${msg})`);
      } finally {
        abortRef.current = null;
        setIsStreaming(false);
      }
    },
    [ensureThreadUuid, isStreaming, params]
  );

  return { messages, isStreaming, send, error };
}
